'use client';

import { useTheme } from '@emotion/react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useState } from 'react';
import { Container } from '../../layout/Container';
import { abcRuArray, DEVICE_SCREENS_MAX_STRING } from '../../../lib/consts';
import { BreadCrumbs } from '../../blocks/BreadCrumbs';
import { H1 } from '../../styled/typography';
import CloseSVG from '../../../public/img/close.svg';
import LoupSVG from '../../../public/img/loupe.svg';
import FilterSVG from '../../../public/img/filter.svg';
import { PersonCard } from '../../person/person-card';
import { CustomCheckBox } from '../../form/form';
import { http } from '../../../lib/api/http';
import { V3 } from '../../../lib/api/urls';
import { activeStatus } from '../../../constants/events';
import { ButtonFilterPersons, ButtonShowMore } from '../../Buttons';
import { StandardButton } from '../../styled/Button';
import { Subscription } from '../../about/Subscription';
import { MobileApps } from '../../about/MobileApps';
import { PersonFilter } from '../../filter/PersonFilter';
import { Specialization } from '../../../models/specialization';
import { City } from '../../../models';
import { PersonInListV3 } from '../../../models/person';
import { extractUtmParams } from '../../../utils/utm';
import { objectToSearchParams } from '../../../utils/objectToSearchParams';
import { withSuspense } from '../../../utils/withSuspense';

type ICatalogPersonsPageProps = {
  currentCity: City;
  personsList: { results: PersonInListV3[]; next: string | null };
  specializationsList: { results: Specialization[] };
  startQuery: {
    last_name__startswith: string | [];
    pages: string[];
    prof: Specialization[];
  };
};

function CatalogPersonsPageInner({
  currentCity,
  personsList,
  specializationsList,
  startQuery,
}: ICatalogPersonsPageProps) {
  const LIMIT_CARDS = 6;
  const theme = useTheme();

  const router = useRouter();
  const searchParams = useSearchParams();

  const [persons, setPersons] = useState(personsList);
  const [isNewData, setIsNewData] = useState(false);
  const defaultQuery: {
    last_name__startswith: string | [];
    pages: number[];
    prof: Specialization[];
  } = {
    prof: [],
    last_name__startswith: [],
    pages: [],
  };
  const [query, setQuery] = useState<{
    last_name__startswith: string | [];
    pages: number[];
    prof: Specialization[];
  }>({ ...startQuery, pages: startQuery.pages.map((item) => Number(item)) });
  const [isMobileFilter, setIsMobileFilter] = useState(false);

  const onCheckSpec = (spec: Specialization) => {
    const profs = [...query.prof];
    for (let i = 0; i < profs.length; i += 1) {
      if (profs[i].id === spec.id) {
        profs.splice(i, 1);
        return setQuery({ ...query, prof: profs });
      }
    }
    profs.push(spec);
    return setQuery({ ...query, prof: profs });
  };

  function handleSelect({
    profs,
    letter,
    close,
  }: {
    profs: Specialization[];
    letter: string | [];
    close: boolean;
  }) {
    setIsMobileFilter(!close);
    const params = {
      last_name__startswith: letter,
      prof: profs,
    };
    return setQuery({ ...query, ...params });
  }

  useEffect(() => {
    setTimeout(
      () => window.scrollTo({ top: 0, left: 0, behavior: 'instant' }),
      0
    );
  }, [isMobileFilter]);

  useEffect(() => {
    let pathname = `/person-catalog/`;
    const utmParams = extractUtmParams(searchParams);
    if (query.prof.length === 1) {
      pathname = `${pathname}${query.prof[0].slug}/`;
    }

    router.push(
      `${pathname}?${objectToSearchParams({
        last_name__startswith: query.last_name__startswith,
        specializations:
          query.prof.length > 1 ? query.prof.map((item) => item.slug) : [],
        page: query.pages,
        ...utmParams.utmObject,
      }).toString()}`
    );

    http
      .get(
        V3.persons,
        {
          limit:
            query.pages.length === 2
              ? isNewData
                ? LIMIT_CARDS
                : (Number(query.pages[1]) - Number(query.pages[0]) + 1) *
                  LIMIT_CARDS
              : LIMIT_CARDS,
          offset: query.pages[0]
            ? isNewData
              ? (Number(query.pages[1]) - 1) * LIMIT_CARDS
              : (Number(query.pages[0]) - 1) * LIMIT_CARDS
            : 0,
          is_web: 'true',
          status: activeStatus,
          last_name__startswith: query.last_name__startswith,
          specialization__in: query.prof.map((spec) => spec.id),
        },
        null,
        { arrayFormat: 'comma' }
      )
      .then((res) => {
        if (!isNewData) {
          setPersons(res);
        } else {
          setPersons({
            ...res,
            results: persons.results.concat(res.results),
          });
        }
      });
    setIsNewData(false);
  }, [query]);

  return (
    <Container
      css={{
        maxWidth: 1280,
        padding: '15px 12px',
        [DEVICE_SCREENS_MAX_STRING.md]: {
          padding: '15px 0',
        },
      }}
    >
      {isMobileFilter ? (
        <PersonFilter
          query={query}
          specializationsList={specializationsList.results}
          persons={personsList}
          onSelect={({
            profs,
            letter,
            close,
          }: {
            profs: Specialization[];
            letter: string | [];
            close: boolean;
          }) => handleSelect({ profs, letter, close })}
        />
      ) : (
        <>
          <BreadCrumbs
            currentCity={currentCity}
            items={[{ title: 'Каталог персон' }]}
            css={{
              display: 'block',
              [DEVICE_SCREENS_MAX_STRING.sm]: {
                display: 'none',
              },
            }}
          />
          <H1
            css={{
              fontFamily: 'Montserrat',
              color: theme.colors.text,
              fontWeight: 700,
              fontSize: 32,
              marginTop: 24,
              [DEVICE_SCREENS_MAX_STRING.md]: {
                marginTop: 12,
                padding: '0 8px',
              },
            }}
          >
            Каталог персон
          </H1>
          <main
            css={{
              fontFamily: 'Golos',
              color: theme.colors.black,
              [DEVICE_SCREENS_MAX_STRING.md]: {
                marginBottom: 60,
              },
            }}
          >
            <div
              css={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                paddingTop: 40,
                [DEVICE_SCREENS_MAX_STRING.lg]: {
                  flexDirection: 'column',
                },
              }}
            >
              <div
                css={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 20,
                  width: '25%',
                  [DEVICE_SCREENS_MAX_STRING.lg]: {
                    width: '100%',
                  },
                  [DEVICE_SCREENS_MAX_STRING.md]: {
                    display: 'none',
                  },
                }}
              >
                <div>
                  <button
                    type='button'
                    css={{
                      display: 'flex',
                      gap: 9,
                      alignItems: 'center',
                      color: '#BFC1C3',
                      fontSize: 14,
                      fontWeight: 400,
                      ':hover': {
                        color: theme.colors.red,
                        svg: {
                          fill: theme.colors.red,
                        },
                      },
                    }}
                    onClick={() => {
                      setIsNewData(false);
                      setQuery(defaultQuery);
                    }}
                  >
                    <div>Сбросить фильтры</div>
                    <CloseSVG
                      css={{ width: 12, height: 12, fill: '#BFC1C3' }}
                    />
                  </button>
                </div>
                <div
                  css={{
                    marginBottom: 16,
                    label: {
                      fontFamily: 'Golos, Arial',
                      color: '#333',
                      fontSize: 12,
                      fontWeight: 400,
                    },
                  }}
                >
                  {specializationsList.results.map((spec: Specialization) => (
                    <div
                      key={`SpecWrapper__${spec.id}`}
                      css={{ padding: '8px 8px 8px 0' }}
                    >
                      <CustomCheckBox
                        value={String(spec.id)}
                        callback={() => {
                          onCheckSpec(spec);
                        }}
                        label={spec.title}
                        checked={
                          !!query.prof.find(
                            (specItem) => specItem.id === spec.id
                          )
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div
                css={{
                  width: '75%',
                  [DEVICE_SCREENS_MAX_STRING.lg]: {
                    width: '100%',
                  },
                }}
              >
                <div
                  css={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                    gap: 6,
                    padding: '0 8px',
                    [DEVICE_SCREENS_MAX_STRING.md]: {
                      display: 'none',
                    },
                  }}
                >
                  {abcRuArray.map((letter) => (
                    <button
                      key={`LetterButton__${letter}`}
                      css={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'flex-end',
                        fontSize: 12,
                        color:
                          query.last_name__startswith === letter
                            ? theme.colors.text
                            : '#BFC1C3',
                        ':hover': {
                          color: theme.colors.red,
                        },
                      }}
                      type='button'
                      onClick={() =>
                        setQuery({
                          ...query,
                          last_name__startswith:
                            query.last_name__startswith === letter
                              ? []
                              : letter,
                        })
                      }
                    >
                      {letter}
                    </button>
                  ))}
                </div>
                <div
                  css={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 20,
                    marginBottom: 20,
                    padding: '0 8px',
                  }}
                />
                {persons.results && persons.results.length ? (
                  <>
                    <div
                      css={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 1fr',
                        [DEVICE_SCREENS_MAX_STRING.md]: {
                          gridTemplateColumns: '1fr 1fr',
                        },
                        [DEVICE_SCREENS_MAX_STRING.sm]: {
                          gridTemplateColumns: '1fr',
                        },
                      }}
                    >
                      {persons.results.map((item) => (
                        <PersonCard
                          key={`PersonCard__${item.id}`}
                          item={item}
                        />
                      ))}
                    </div>

                    <div css={{ padding: '0 8px' }} />
                  </>
                ) : (
                  <div
                    css={{
                      fontFamily: 'Montserrat',
                      width: '100%',
                      minHeight: 400,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <LoupSVG css={{ marginBottom: 30 }} />
                    <div
                      css={{
                        marginBottom: 4,
                        fontSize: 18,
                        fontWeight: 'bold',
                        color: '#333',
                      }}
                    >
                      К сожалению, ничего не нашлось.
                    </div>
                    <div
                      css={{
                        fontSize: 12,
                        color: '#333',
                      }}
                    >
                      Попробуйте изменить варианты или фильтры поиска
                    </div>
                  </div>
                )}
                <div
                  css={{
                    display: 'none',
                    justifyContent: 'center',
                    marginBottom: 16,
                    [DEVICE_SCREENS_MAX_STRING.md]: {
                      display: 'flex',
                    },
                  }}
                >
                  <ButtonFilterPersons
                    callback={() => setIsMobileFilter((prevVal) => !prevVal)}
                  >
                    Фильтры и сортировка
                    <FilterSVG
                      css={{
                        fill: '#fff',
                        width: 16,
                        height: 16,
                        marginLeft: 6,
                      }}
                    />
                  </ButtonFilterPersons>
                </div>
                {persons.next && (
                  <div
                    css={{
                      display: 'flex',
                      gap: 10,
                      padding: '0 8px',
                    }}
                  >
                    <ButtonShowMore
                      callback={() => {
                        setIsNewData(true);
                        setQuery({
                          ...query,
                          pages: query.pages[1]
                            ? [1, Number(query.pages[1]) + 1]
                            : [1, 2],
                        });
                      }}
                    />
                    <StandardButton callback={() => window.scrollTo(0, 0)}>
                      Наверх
                    </StandardButton>
                  </div>
                )}
              </div>
            </div>
            <div
              css={{
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            />
            <div
              css={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 16,
                marginBottom: '4rem',
                marginTop: 80,
                [DEVICE_SCREENS_MAX_STRING.xl]: {
                  gridTemplateColumns: '1fr',
                },
                [DEVICE_SCREENS_MAX_STRING.md]: {
                  display: 'none',
                },
              }}
            >
              <Subscription currentCity={currentCity} />
              <MobileApps />
            </div>
          </main>
        </>
      )}
    </Container>
  );
}

export const CatalogPersonsPage = withSuspense(CatalogPersonsPageInner);
